import { ButtonDefinition, ButtonIcon, ModalDialog, showDialog } from "./skyward.ui.modalLayer";
import { changeView } from "./skyward.ui.browse";
import { displayValidationMessages, ValidationData } from "./skyward.ui.validation";
import { registerEvents } from "./skyward.ui.events";
import { getBrowseById } from "./skyward.ui.browse.list";
import { virtualDir } from "./skyward.base.global";
import { reportException, SkywardException } from "#skyward_error";
import { JQueryInput } from "./types";

interface BrowseParameters
{
    pageModule: string;
    pageObject: string;
    pageScreen: string;
    browseName: string;
    taskName: string;
    browseId: string;
    browseViewEditorHash?: string;
    postData?: string;
}

type ViewPopupHandler = (
    this: JQuery,
    popupContent: JQuery,
    dialog: ModalDialog,
    browseId: string,
    validationContainer: JQuery,
    browseParameters: BrowseParameters
) => JQuery.Promise<unknown>;

interface SaveChangesData
{
    name: string | number | string[] | undefined;
    warninghash: string;
    postData?: string;
}

interface ButtonClickResponse extends ValidationData
{
    success: boolean;
    id?: number;
}

interface ViewPopupButtonData
{
    browseId: string;
    controller: string;
    action: string;
    id: number;
    modifiedTextRowId: string;
    applyButtonRowId: string;
    disableDuplicateValidation?: boolean;
    disableOtherHelpLabelValidationId: string;
    disableOtherTextboxValidationId: string;
    skywardIsDefaultId?: number;
    browsePrimaryKey?: number;
}

const events =
{
    "open": buildDefault
};

registerEvents("viewPopup", events);

/**
 * Builds the popup for choosing and managing a browse's saved views.
 * @param button The button that opened the popup.
 * @param browseParameters Describes the browse the popup is attached to.
 */
function build(button: JQuery, browseParameters: BrowseParameters)
{
    const dialog = buildPopup();
    dialog.content.on("contentLoaded", setupPopup);

    function buildPopup()
    {
        let buildPopupAction = "BuildViewOptionsViewpart";

        const title = "Views";
        const cancelButton: ButtonDefinition = { id: "close", text: "Close", click: () => dialog.close(), icon: ButtonIcon.Close };
        const options =
        {
            anchorTo: button
        };

        if (browseParameters.browseViewEditorHash)
        {
            buildPopupAction += "ForBrowseViewEditor";
        }

        const buildPopupFunc = getBuildPopupFunc(browseParameters, buildPopupAction);

        return showDialog(title, buildPopupFunc, [cancelButton], options);
    }

    function setupPopup(this: JQueryInput)
    {
        const popupContent = $(this);
        const popupData = popupContent.find("#browseViewOptions").data() as { browseId: string };
        const baseErrorsDiv = popupContent.find("#browseViewBaseErrors");
        const clickFuncArgs = [popupContent, dialog, popupData.browseId, baseErrorsDiv, browseParameters];

        let isRunning = false;
        const actionDefinitions: { selector: string; clickFunc: ViewPopupHandler }[] = [
            { selector: "#resetAndApplyButton", clickFunc: resetAndApply },
            { selector: "#browseViewSaveChangesButton", clickFunc: saveChanges },
            { selector: "#browseViewSaveAsButton", clickFunc: saveAs },
            { selector: "a.delete", clickFunc: deleteItem },
            { selector: "a.apply.view", clickFunc: apply },
            { selector: "a.reset.view", clickFunc: reset }
        ];

        for (const actionDefinition of actionDefinitions)
        {
            popupContent.on("click", actionDefinition.selector, actionDefinition, debounce);
        }

        function debounce(this: JQueryInput, event: JQuery.TriggeredEvent)
        {
            const actionButton = $(this);
            if (actionButton.hasClass("ui-disabled") || actionButton.hasClass("hidden"))
            {
                return;
            }

            if (!isRunning)
            {
                isRunning = true;

                const def = event.data;
                def.clickFunc.apply(actionButton, clickFuncArgs).always(function ()
                {
                    isRunning = false;
                });
            }
        }
    }
}

function buildDefault(this: JQueryInput)
{
    const viewButton = $(this);
    const browseId = (viewButton.data() as { browseId: string }).browseId;
    const browse = getBrowseById(browseId);

    if (browse === undefined)
    {
        reportException(new SkywardException(`Cannot open the view popup: no browse was found for id '${browseId}'.`));
        return;
    }

    const browseViewEditorHash = browse.data?.browseViewEditorHash as string | undefined;
    const popupBuilderParameters: BrowseParameters =
    {
        pageModule: browse.pageModule,
        pageObject: browse.pageObject,
        pageScreen: browse.pageScreen,
        browseName: browse.browseName,
        taskName: browse.taskName,
        browseId: browse.id,
        browseViewEditorHash
    };

    if (browseViewEditorHash)
    {
        popupBuilderParameters.postData = JSON.stringify(browse.data);
    }

    build(viewButton, popupBuilderParameters);
}

function resetAndApply(this: JQuery, _popupContent: JQuery, dialog: ModalDialog, browseId: string, validationContainer: JQuery, _browseParameters: BrowseParameters): JQuery.Promise<unknown>
{
    return menuClosingButtonClick(this, null, dialog, browseId, validationContainer);
}

function saveChanges(this: JQuery, popupContent: JQuery, dialog: ModalDialog, browseId: string, validationContainer: JQuery, browseParameters: BrowseParameters): JQuery.Promise<unknown>
{
    const textbox = popupContent.find("#currentNameTextbox");
    const saveData: SaveChangesData =
    {
        name: textbox.val(),
        warninghash: JSON.stringify(dialog.content.data("warninghash"))
    };

    if (browseParameters.browseViewEditorHash)
    {
        saveData.postData = browseParameters.postData;
    }

    return menuClosingButtonClick(this, saveData, dialog, browseId, validationContainer);
}

function saveAs(this: JQuery, popupContent: JQuery, dialog: ModalDialog, browseId: string, validationContainer: JQuery, _browseParameters: BrowseParameters): JQuery.Promise<unknown>
{
    const textbox = popupContent.find(".saveAsTextbox");
    const saveData =
    {
        name: textbox.val()
    };

    return menuClosingButtonClick(this, saveData, dialog, browseId, validationContainer);
}

function deleteItem(this: JQuery, _popupContent: JQuery, dialog: ModalDialog, browseId: string, validationContainer: JQuery, _browseParameters: BrowseParameters): JQuery.Promise<unknown>
{
    const buttonData = this.data() as ViewPopupButtonData;
    const postData =
    {
        skywardIsDefaultId: buttonData.skywardIsDefaultId,
        browsePrimaryKey: buttonData.browsePrimaryKey
    };

    return menuClosingButtonClick(this, postData, dialog, browseId, validationContainer);
}

function apply(this: JQuery, _popupContent: JQuery, dialog: ModalDialog, browseId: string, _validationContainer: JQuery, _browseParameters: BrowseParameters): JQuery.Promise<unknown>
{
    const buttonData = this.data() as ViewPopupButtonData;

    return changeView(browseId, { viewId: buttonData.id }).always(function ()
    {
        dialog.close();
    });
}

function reset(this: JQuery, _popupContent: JQuery, dialog: ModalDialog, _browseId: string, validationContainer: JQuery, _browseParameters: BrowseParameters): JQuery.Promise<unknown>
{
    const button = this;
    const buttonData = button.data() as ViewPopupButtonData;

    return $.ajax(
        {
            url: `${virtualDir}SkySys/${buttonData.controller}/${buttonData.action}/${buttonData.id}`,
            type: "POST",
            dataType: "JSON",
            activityIndicator: { hideGlass: true },
            success: (response: ButtonClickResponse) =>
            {
                if (response.success === true)
                {
                    $(buttonData.modifiedTextRowId).hide();
                    button.css("visibility", "hidden");
                    $(buttonData.applyButtonRowId).data().id = response.id;
                }
                else
                {
                    displayValidationMessages(response, dialog.content, validationContainer);
                }
            }
        });
}

function menuClosingButtonClick(button: JQuery, postData: JQuery.PlainObject | null, dialog: ModalDialog, browseId: string, validationContainer: JQuery)
{
    const buttonData = button.data() as ViewPopupButtonData;

    return $.ajax(
        {
            url: `${virtualDir}SkySys/${buttonData.controller}/${buttonData.action}/${buttonData.id}`,
            type: "POST",
            dataType: "JSON",
            data: postData ?? undefined,
            activityIndicator: { hideGlass: true },
            success: (response: ButtonClickResponse) =>
            {
                let disabledControl: JQuery | undefined;

                if (response.success === true)
                {
                    dialog.close();
                    changeView(browseId, { viewId: response.id });
                }
                else
                {
                    //ToDo: We have 2 textboxes that display validation for the same field on an object. Ideally we'd like to show validation messages only on the textbox the user
                    // was actually using. Until we get a PR to allow a validation rule to specify a specific control, this is a workaround.
                    if (buttonData.disableDuplicateValidation === true)
                    {
                        disabledControl = disableDuplicateValidation(button);
                    }

                    displayValidationMessages(response, dialog.content, validationContainer);

                    if (disabledControl !== undefined)
                    {
                        restoreDuplicateValidation(disabledControl);
                    }
                }
            }
        });
}

function getBuildPopupFunc(browseParameters: BrowseParameters, buildPopupAction: string)
{
    const requestData =
    {
        pageScreen: browseParameters.pageScreen,
        browseName: browseParameters.browseName.replace("detailsPanel_", ""),
        taskName: browseParameters.taskName,
        browseId: browseParameters.browseId,
        moduleName: browseParameters.pageModule,
        objectName: browseParameters.pageObject,
        postData: browseParameters.postData
    };

    return () =>
    {
        return $.ajax(
            {
                url: `${virtualDir}SkySys/BrowseView/${buildPopupAction}`,
                type: "POST",
                dataType: "viewparthtml",
                activityIndicator: { hideGlass: true },
                data: requestData
            });
    };
}

function restoreDuplicateValidation(button: JQuery): void
{
    const buttonData = button.data() as ViewPopupButtonData;

    const validationLabel = $(buttonData.disableOtherHelpLabelValidationId);
    validationLabel.attr("data-validate-message-for", validationLabel.attr("data-original-validate-message-for") ?? "");

    const validationTextbox = $(buttonData.disableOtherTextboxValidationId);
    validationTextbox.attr("data-validate-control-for", validationTextbox.attr("data-original-validate-control-for") ?? "");
}

function disableDuplicateValidation(button: JQuery): JQuery
{
    const buttonData = button.data() as ViewPopupButtonData;

    const validationLabel = $(buttonData.disableOtherHelpLabelValidationId);
    validationLabel.attr("data-original-validate-message-for", validationLabel.attr("data-validate-message-for") ?? "");
    validationLabel.attr("data-validate-message-for", "");

    const validationTextbox = $(buttonData.disableOtherTextboxValidationId);
    validationTextbox.attr("data-original-validate-control-for", validationTextbox.attr("data-validate-control-for") ?? "");
    validationTextbox.attr("data-validate-control-for", "");

    return button;
}
